// The "what's going on with Arlo right now" engine. Pure functions of the
// event log and the clock, shared by the app and the server's /api/summary.

import { isExperience } from './visitors.ts';
import { OPEN_SLEEP_CAP, sleepWithin } from './sleep.ts';
import { awakeWindowMinutes, sleepTargetHours, toiletIntervalMinutes } from './age.ts';
import { DAY, MINUTE, dayStart, formatTime, startOfDay } from './time.ts';
import type { LogEvent, Millis } from './types.ts';

export type Urgency = 'ok' | 'soon' | 'now' | 'overdue';

export interface ToiletStatus {
  lastWee?: LogEvent<'wee'>;
  lastPoo?: LogEvent<'poo'>;
  /** When the next toilet trip is due. Undefined while asleep. */
  dueAt?: Millis;
  reason: string;
  urgency: Urgency;
  /**
   * Nothing has been logged for a while, so the prediction is guesswork.
   * The UI switches to a gentle routine reminder instead of "overdue".
   */
  stale?: boolean;
  /** Most recent log of any kind. */
  lastLogAt?: Millis;
}

export interface SleepStatus {
  asleep: boolean;
  /** Current nap, if asleep. */
  current?: LogEvent<'sleep'>;
  /** When he last woke up (if we know). */
  wokeAt?: Millis;
  awakeFor?: Millis;
  napDueAt?: Millis;
  napUrgency: Urgency;
  last24h: Millis;
  target: [number, number];
  /**
   * No nap logged for far longer than he could stay awake — naps just weren't
   * logged. Don't claim "awake 12 hours"; ask for the next nap instead.
   */
  stale?: boolean;
}

export interface DayStats {
  date: Millis;
  weeOutside: number;
  weeInside: number;
  pooOutside: number;
  pooInside: number;
  meals: number;
  sleepMs: number;
  walkMs: number;
  playMs: number;
  trainingSessions: number;
  socialExposures: number;
}

const byTimeDesc = (a: LogEvent, b: LogEvent) => b.at - a.at;

export function latest<T extends LogEvent['type']>(events: LogEvent[], type: T, pred?: (e: LogEvent<T>) => boolean) {
  let best: LogEvent<T> | undefined;
  for (const e of events) {
    if (e.type !== type) continue;
    const ev = e as LogEvent<T>;
    if (pred && !pred(ev)) continue;
    if (!best || ev.at > best.at) best = ev;
  }
  return best;
}

export function sleepStatus(events: LogEvent[], ageWeeks: number, now: Millis): SleepStatus {
  const sleeps = events.filter((e): e is LogEvent<'sleep'> => e.type === 'sleep').sort(byTimeDesc);
  // A nap open for over 12 hours was never ended — he's not still asleep.
  const current = sleeps.find((s) => s.end === undefined && s.at <= now && now - s.at < OPEN_SLEEP_CAP);
  const lastEnded = sleeps.find((s) => s.end !== undefined && s.end <= now);
  const target = sleepTargetHours(ageWeeks);
  const last24h = sleepWithin(sleeps, now - DAY, now);

  if (current) {
    return { asleep: true, current, napUrgency: 'ok', last24h, target };
  }
  const wokeAt = lastEnded?.end;
  const awakeFor = wokeAt !== undefined ? now - wokeAt : undefined;
  const window = awakeWindowMinutes(ageWeeks) * MINUTE;
  // Several times his awake window (and at least 4 hours) with no nap logged: the log has a gap, not a sleepless puppy.
  if (awakeFor !== undefined && awakeFor > Math.max(4 * 60 * MINUTE, window * 3)) return { asleep: false, wokeAt, awakeFor, napUrgency: 'ok', last24h, target, stale: true };
  const napDueAt = wokeAt !== undefined ? wokeAt + window : undefined;
  let napUrgency: Urgency = 'ok';
  if (napDueAt !== undefined) {
    const left = napDueAt - now;
    if (left < -30 * MINUTE) napUrgency = 'overdue';
    else if (left <= 0) napUrgency = 'now';
    else if (left < 15 * MINUTE) napUrgency = 'soon';
  }
  return { asleep: false, wokeAt, awakeFor, napDueAt, napUrgency, last24h, target };
}

/** Total sleep in [from, to] — overlapping naps count once, open naps at most 12 hours (domain/sleep.ts). */
export { sleepWithin } from './sleep.ts';

export function durationWithin(events: LogEvent[], type: LogEvent['type'], from: Millis, to: Millis): Millis {
  let total = 0;
  for (const e of events) {
    if (e.type !== type || e.end === undefined) continue;
    const s = Math.max(e.at, from);
    const end = Math.min(e.end, to);
    if (end > s) total += end - s;
  }
  return total;
}

/**
 * Predict the next toilet trip. Puppies need to go:
 *  - straight after waking up,
 *  - 5–20 minutes after eating or drinking lots,
 *  - after play, training or excitement,
 *  - and at least every `toiletIntervalMinutes(age)` while awake.
 */
export function toiletStatus(allEvents: LogEvent[], ageWeeks: number, now: Millis, sleep?: SleepStatus, leadMinutes = 0): ToiletStatus {
  // An entry timed in the future (a mistyped time, a device with its clock
  // off) mustn't turn the countdown into "-6h since wee". A few minutes of
  // clock drift between devices is fine.
  const events = allEvents.some((e) => e.at > now + 5 * MINUTE) ? allEvents.filter((e) => e.at <= now + 5 * MINUTE) : allEvents;
  const lastWee = latest(events, 'wee');
  const lastPoo = latest(events, 'poo');
  const s = sleep ?? sleepStatus(events, ageWeeks, now);
  if (s.asleep) {
    return {
      lastWee,
      lastPoo,
      reason: 'Asleep — take him straight out when he wakes.',
      urgency: 'ok',
    };
  }

  const lastToilet = Math.max(lastWee?.at ?? 0, lastPoo?.at ?? 0);
  const interval = toiletIntervalMinutes(ageWeeks) * MINUTE;
  const candidates: { at: Millis; reason: string }[] = [];

  // Puppies hold on much longer asleep, so naps pause the regular clock.
  const slept = lastToilet ? sleepWithin(events, lastToilet, now) : 0;
  if (lastToilet) candidates.push({ at: lastToilet + interval + slept, reason: `Regular trip — every ~${Math.round(interval / MINUTE)} min while awake` });
  else candidates.push({ at: now, reason: 'No toilet trips logged yet' });

  if (s.wokeAt !== undefined && s.wokeAt > lastToilet)
    candidates.push({ at: s.wokeAt, reason: 'Just woke up' }); // straight out — not a countdown

  const meal = latest(events, 'meal');
  if (meal && meal.at > lastToilet) candidates.push({ at: meal.at + 15 * MINUTE, reason: 'After his meal' });

  for (const type of ['play', 'training', 'walk'] as const) {
    const e = latest(events, type, (x) => !(x.type === 'walk' && x.data.auto === 'pending'));
    // Only finished activities count — an outing in progress isn't "back" yet.
    const endedAt = e?.type === 'training' ? e.at : e?.end;
    // A game winds him up: out within minutes. A five-minute training session is calm — more like a meal.
    if (e && endedAt !== undefined && endedAt > lastToilet && endedAt <= now)
      candidates.push({ at: endedAt + (type === 'training' ? 15 : 5) * MINUTE, reason: type === 'walk' ? 'Back from an outing' : `After ${type}` });
  }

  // In a flat it takes a few minutes to reach the toilet spot, so the
  // reminder fires that much earlier (never earlier than the trigger itself
  // for "just woke up", which is already immediate).
  const lead = leadMinutes * MINUTE;
  for (const c of candidates) if (c.reason !== 'Just woke up') c.at -= lead;
  const earliest = candidates.reduce((a, b) => (b.at < a.at ? b : a));
  // Several reasons at once (lunch, then a training session): name the meal — it's the one that matters.
  const meal15 = candidates.find((c) => c.reason === 'After his meal' && c.at - earliest.at <= 10 * MINUTE);
  const best = meal15 && earliest.reason !== 'Just woke up' ? { at: earliest.at, reason: meal15.reason } : earliest;
  const left = best.at - now;
  // Forgiving mode: if nobody has logged anything for a while, don't pretend
  // we know he's hours "overdue" — people forget to log, and that's fine.
  // Only what people log counts: a camera spotting him isn't anyone keeping track.
  const lastLogAt = events.reduce((m, e) => (e.type !== 'tracker' && e.type !== 'camera' && e.source !== 'automation' && e.at <= now && e.at > m ? e.at : m), 0) || undefined;
  const quietFor = now - Math.max(lastLogAt ?? 0, s.wokeAt ?? 0);
  // Two hours of nothing at his age nearly always hides an unlogged nap — don't sound the alarm on a sleeping puppy.
  if (!lastLogAt || quietFor > Math.max(2 * 60 * MINUTE, 2 * interval)) {
    return { lastWee, lastPoo, dueAt: undefined, reason: 'Nothing logged recently', urgency: 'ok', stale: true, lastLogAt };
  }
  let urgency: Urgency = 'ok';
  if (left < -20 * MINUTE) urgency = 'overdue';
  else if (left <= 0) urgency = 'now';
  else if (left < 10 * MINUTE) urgency = 'soon';
  // "Just woke up" stops being true after a few minutes: then he simply hasn't been out since.
  // Likewise "after his meal" (or a game, or training) once it's long past: say when he was last out instead.
  const trigger = ['After his meal', 'After play', 'After training', 'Back from an outing'].includes(best.reason);
  const reason =
    best.reason === 'Just woke up' && s.wokeAt !== undefined && now - s.wokeAt > 10 * MINUTE
      ? 'Not out since he woke'
      : trigger && now - best.at > 45 * MINUTE && lastToilet
        ? `Not out since ${formatTime(lastToilet)}`
        : best.reason;
  return { lastWee, lastPoo, dueAt: best.at, reason, urgency, lastLogAt };
}

export function dayStats(events: LogEvent[], day: Millis): DayStats {
  const from = startOfDay(day);
  const to = dayStart(from, 1);
  const inDay = events.filter((e) => e.at >= from && e.at < to);
  const count = (pred: (e: LogEvent) => boolean) => inDay.filter(pred).length;
  return {
    date: from,
    weeOutside: count((e) => e.type === 'wee' && e.data.where === 'outside'),
    weeInside: count((e) => e.type === 'wee' && e.data.where === 'inside'),
    pooOutside: count((e) => e.type === 'poo' && e.data.where === 'outside'),
    pooInside: count((e) => e.type === 'poo' && e.data.where === 'inside'),
    meals: count((e) => e.type === 'meal'),
    sleepMs: sleepWithin(events, from, Math.min(to, Date.now())),
    walkMs: durationWithin(events, 'walk', from, to),
    playMs: durationWithin(events, 'play', from, to),
    trainingSessions: count((e) => e.type === 'training'),
    socialExposures: count(isExperience),
  };
}

export function toiletSuccess(stats: Pick<DayStats, 'weeOutside' | 'weeInside' | 'pooOutside' | 'pooInside'>): number | undefined {
  const outside = stats.weeOutside + stats.pooOutside;
  const total = outside + stats.weeInside + stats.pooInside;
  return total ? outside / total : undefined;
}

/** Time since the last indoor accident (or since the first log). */
export function accidentFreeSince(events: LogEvent[]): Millis | undefined {
  const accident = events
    .filter((e) => (e.type === 'wee' || e.type === 'poo') && e.data.where === 'inside')
    .sort(byTimeDesc)[0];
  if (accident) return accident.at;
  const first = events.filter((e) => e.type === 'wee' || e.type === 'poo').sort((a, b) => a.at - b.at)[0];
  return first?.at;
}

/**
 * Hour-of-day histogram of toilet events over the last `days` days —
 * reveals his natural rhythm so trips can be timed before accidents happen.
 */
export function toiletHeatmap(events: LogEvent[], now: Millis, days = 14): { hour: number; outside: number; inside: number }[] {
  const from = now - days * DAY;
  const hours = Array.from({ length: 24 }, (_, hour) => ({ hour, outside: 0, inside: 0 }));
  for (const e of events) {
    if ((e.type !== 'wee' && e.type !== 'poo') || e.at < from) continue;
    const h = new Date(e.at).getHours();
    if (e.data.where === 'outside') hours[h].outside++;
    else hours[h].inside++;
  }
  return hours;
}

/** One-line natural-language status for dashboards and Home Assistant. */
export function headline(t: ToiletStatus, s: SleepStatus, now: Millis): string {
  if (s.asleep && s.current) return `Asleep for ${Math.round((now - s.current.at) / MINUTE)} min`;
  if (t.stale) return 'Nothing logged recently — keep to the usual routine';
  if (t.urgency === 'now' || t.urgency === 'overdue') return `Toilet trip due now — ${t.reason.toLowerCase()}`;
  if (s.napUrgency === 'now' || s.napUrgency === 'overdue') return 'Nap time — he has been awake a while';
  if (t.dueAt) return `Next toilet trip in ${Math.max(0, Math.round((t.dueAt - now) / MINUTE))} min`;
  return 'All good';
}
